import React, { useEffect, useState } from "react";
import { api, trackView, resolveImg } from "../lib/api";
import SEO from "../components/SEO";
import { ArrowUpRight, X } from "lucide-react";

const categories = [
  { v: "", label: "Tous" },
  { v: "site-vitrine", label: "Sites vitrines" },
  { v: "rdv", label: "Prise de RDV" },
  { v: "click-collect", label: "Click & Collect" },
  { v: "refonte", label: "Refontes" },
  { v: "identite", label: "Identité visuelle" },
];

export default function Realisations() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { trackView("/realisations"); }, []);
  useEffect(() => {
    (async () => {
      const params = filter ? { params: { category: filter } } : {};
      const { data } = await api.get("/projects", params);
      setProjects(data);
    })();
  }, [filter]);

  return (
    <div className="pt-32 pb-24 bg-[#09090b] text-white min-h-screen">
      <SEO
        title="Réalisations — Portfolio Blackline Studio"
        description="Découvrez nos derniers projets : sites vitrines, Click & Collect, prises de rendez-vous, refontes et identités visuelles pour des commerces locaux et professionnels."
        keywords="portfolio agence web, réalisations site internet, exemples Click and Collect, projets identité visuelle"
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Portfolio</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl mb-6" data-testid="realisations-title">
          Nos <span className="italic font-light">réalisations</span> récentes.
        </h1>
        <p className="text-zinc-400 max-w-2xl font-light leading-relaxed mb-12">
          Une sélection de projets livrés pour des entreprises ambitieuses : commerces locaux,
          professions libérales et marques émergentes.
        </p>

        <div className="flex flex-wrap gap-2 mb-12 border-b border-white/10 pb-6" data-testid="filter-bar">
          {categories.map((c) => (
            <button
              key={c.v}
              onClick={() => setFilter(c.v)}
              data-testid={`filter-${c.v || "all"}`}
              className={`px-4 py-2 text-sm tracking-wide border transition-all ${
                filter === c.v
                  ? "bg-[#d4af37] text-black border-[#d4af37]"
                  : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {projects.length === 0 ? (
          <div className="border border-dashed border-white/10 p-20 text-center" data-testid="no-projects-msg">
            <p className="text-zinc-500 font-light">Aucune réalisation pour cette catégorie pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {projects.map((p, i) => (
              <div
                key={p.id}
                className="bg-[#0a0a0c] cursor-pointer card-lift group"
                onClick={() => setSelected(p)}
                data-testid={`project-card-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#18181b]">
                  {p.image_url ? (
                    <img src={resolveImg(p.image_url)} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700 font-display text-6xl">{p.title?.[0]}</div>
                  )}
                </div>
                <div className="p-6">
                  <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-2">{p.category}</div>
                  <h3 className="font-display text-xl mb-2">{p.title}</h3>
                  <p className="text-zinc-400 text-sm font-light line-clamp-2">{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Project detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelected(null)}
          data-testid="project-modal"
        >
          <div
            className="bg-[#0a0a0c] border border-white/10 max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-[16/9] bg-[#18181b] relative">
              {selected.image_url && <img src={selected.image_url} alt={selected.title} className="w-full h-full object-cover" />}
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 w-10 h-10 bg-black/60 backdrop-blur flex items-center justify-center hover:bg-black/80" data-testid="modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-8 lg:p-10">
              <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-3">{selected.category}{selected.industry && ` · ${selected.industry}`}</div>
              <h2 className="font-display text-3xl lg:text-4xl mb-4">{selected.title}</h2>
              <p className="text-zinc-300 leading-relaxed mb-8 font-light">{selected.description}</p>
              {selected.features?.length > 0 && (
                <div className="mb-8">
                  <div className="text-xs tracking-[0.2em] text-zinc-500 uppercase mb-4">Fonctionnalités</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selected.features.map((f, i) => (
                      <li key={i} className="text-sm text-zinc-300 flex gap-2"><span className="text-[#d4af37]">—</span> {f}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.demo_url && (
                <a href={selected.demo_url} target="_blank" rel="noreferrer"
                  data-testid="visit-demo-btn"
                  className="inline-flex items-center gap-3 btn-gold px-6 py-3 text-sm">
                  Visiter la démo <ArrowUpRight size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
